import type {ImgHTMLAttributes, JSX} from 'react'
import {getImageProps} from 'next/image'
import {loadImageFromKontentAI} from '@/image-loader'

interface ResponsivePictureProps extends ImgHTMLAttributes<HTMLImageElement> {
	mobileSrcSet?: string
	pictureClassName?: string
}

const ResponsivePicture = ({
	mobileSrcSet,
	pictureClassName,
	sizes,
	...imgProps
}: ResponsivePictureProps): JSX.Element => (
	<picture className={pictureClassName}>
		{mobileSrcSet ? (
			<source
				media="(max-width: 767px)"
				sizes={sizes}
				srcSet={mobileSrcSet}
			/>
		) : null}
		{/* eslint-disable-next-line jsx-a11y/alt-text -- alt is forwarded via imgProps */}
		<img sizes={sizes} {...imgProps} />
	</picture>
)

interface KontentImageAsset {
	url: string
	description: string | null
	height: number | null
	width: number | null
}

interface KontentResponsivePictureProps {
	desktopAsset: KontentImageAsset | null | undefined
	mobileAsset?: KontentImageAsset | null
	className?: string
	fallbackHeight?: number
	fallbackWidth?: number
	fetchPriority?: 'high' | 'low' | 'auto'
	pictureClassName?: string
	priority?: boolean
	quality?: number
	sizes?: string
}

export const KontentResponsivePicture = ({
	desktopAsset,
	mobileAsset,
	className,
	fallbackHeight = 300,
	fallbackWidth = 500,
	fetchPriority,
	pictureClassName,
	priority,
	quality,
	sizes,
}: KontentResponsivePictureProps): JSX.Element | null => {
	if (!desktopAsset) return null

	const distinctMobileAsset =
		mobileAsset !== null && mobileAsset !== desktopAsset
			? mobileAsset
			: null

	const {alt, ...desktopImgProps} = getImageProps({
		alt: desktopAsset.description ?? '',
		fetchPriority,
		height: desktopAsset.height || fallbackHeight,
		loader: loadImageFromKontentAI,
		priority,
		quality,
		sizes,
		src: desktopAsset.url,
		width: desktopAsset.width || fallbackWidth,
	}).props

	const {srcSet: mobileSrcSet} = distinctMobileAsset
		? getImageProps({
				alt: distinctMobileAsset.description ?? '',
				height: distinctMobileAsset.height || fallbackHeight,
				loader: loadImageFromKontentAI,
				quality,
				sizes,
				src: distinctMobileAsset.url,
				width: distinctMobileAsset.width || fallbackWidth,
			}).props
		: {srcSet: undefined}

	return (
		<ResponsivePicture
			alt={alt}
			className={className}
			mobileSrcSet={mobileSrcSet}
			pictureClassName={pictureClassName}
			{...desktopImgProps}
		/>
	)
}
